export function extractMacroTokensFromString(textOrHtml) {
    const tokens = new Set()
    const ranges = getTokenRanges(normalizeTokenWhitespace(convertChipHtmlToTokens(textOrHtml)))
    ranges.forEach((range) => tokens.add(range.tokenId))
    return tokens
}

export function getMacroTokenRanges(textOrHtml) {
    return getTokenRanges(textOrHtml)
}

export function findTokenAtCursor(value, cursorIndex) {
    if (cursorIndex === null || cursorIndex === undefined) return null
    const text = toSafeString(value)
    if (!text) return null
    if (cursorIndex <= 0 || cursorIndex > text.length) return null
    const ranges = getTokenRanges(text)
    for (const range of ranges) {
        if (cursorIndex > range.start && cursorIndex < range.end) {
            return { start: range.start, end: range.end, tokenId: range.tokenId }
        }
    }
    return null
}

export function replaceTokenAtCursor(value = "", cursorIndex, newTokenString) {
    const text = toSafeString(value)
    const insertion = newTokenString ?? ""
    const match = findTokenAtCursor(text, cursorIndex)
    if (match) {
        return `${text.slice(0, match.start)}${insertion}${text.slice(match.end)}`
    }
    const index = clampCursor(cursorIndex ?? text.length, text.length)
    return `${text.slice(0, index)}${insertion}${text.slice(index)}`
}

export function collectMacroTokensFromSchedule(schedule, tokenSet = new Set()) {
    if (!schedule) return tokenSet
    collectTokens(schedule.title, tokenSet)
    collectTokens(schedule.intro?.content, tokenSet)
    collectTokens(schedule.intro?.note, tokenSet)
    for (const task of schedule.tasks ?? []) {
        collectTokens(task?.title, tokenSet)
        collectTokens(task?.content, tokenSet)
        collectTokens(task?.note, tokenSet)
        if (task?.componentRef) tokenSet.add(`cmp:${task.componentRef}`)
    }
    return tokenSet
}

function getTokenRanges(value) {
    const text = toSafeString(value)
    if (!text) return []
    const ranges = []
    let index = 0
    while (index < text.length) {
        const start = text.indexOf("$", index)
        if (start === -1) break
        const next = text[start + 1]
        if (next === "$") {
            index = start + 2
            continue
        }
        if (!isTokenStartChar(next)) {
            index = start + 1
            continue
        }
        let end = start + 1
        while (end < text.length && isTokenChar(text[end])) {
            end++
        }
        if (text[end] !== "$") {
            index = end
            continue
        }
        const tokenId = text.slice(start + 1, end)
        if (isValidToken(tokenId)) {
            ranges.push({ start, end: end + 1, tokenId })
        }
        index = end + 1
    }
    return ranges
}

function isValidToken(tokenId) {
    return /^[A-Za-z0-9_](?:[A-Za-z0-9_:./-]*[A-Za-z0-9_])?$/.test(tokenId)
}

export function collectMacroTokensFromContent(content, tokenSet) {
    if (!content) return
    if (!content.includes("$") && !content.includes("data-macro-token")) return
    for (const token of extractMacroTokensFromString(content)) tokenSet.add(token)
}

function collectTokens(value, tokenSet) {
    collectMacroTokensFromContent(value, tokenSet)
}

function isTokenChar(char) {
    return char === "-" || char === "_" || char === ":" || char === "." || char === "/" || isAlphaNumeric(char)
}

function isTokenStartChar(char) {
    return char === "_" || isAlphaNumeric(char)
}

function isAlphaNumeric(char) {
    if (!char) return false
    const code = char.charCodeAt(0)
    return (code >= 48 && code <= 57) || (code >= 65 && code <= 90) || (code >= 97 && code <= 122)
}

function toSafeString(value) {
    if (value === null || value === undefined) return ""
    return `${value}`
}

function clampCursor(cursorIndex, length) {
    if (cursorIndex < 0) return 0
    if (cursorIndex > length) return length
    return cursorIndex
}

function normalizeTokenWhitespace(value) {
    if (!value) return value
    return value.replace(/\$([^$]+)\$/g, normalizeTokenMatch)
}

function normalizeTokenMatch(match, inner) {
    const normalized = inner.replace(/[\s\u00a0]+/g, "")
    if (!normalized) return match
    return `$${normalized}$`
}

function convertChipHtmlToTokens(value) {
    if (!value) return value
    return `${value}`.replace(/<span[^>]*?\bdata-macro-token="([^"]*)"[^>]*?>[\s\S]*?<\/span>/g, replaceChipMatch)
}

function replaceChipMatch(match, tokenId) {
    return `$${tokenId}$`
}
